import Component from '../Component';

export default class Popup extends Component {
  constructor(theme, element) {
    super(theme, element);

    // Storage configuration keys
    this.STORAGE_KEY = 'wetheme-popup';
    this.VISIBILITY_KEY = 'popup-visible';
    
    // Check local storage support early as it's used throughout
    this.hasLocalStorage = this._isLocalStorageSupported();
    
    // Popup configuration flags and timing
    this.testMode = this.element.dataset.popupTestMode === 'true';
    this.isFullscreen = this.element.dataset.desktopType === 'fullscreen';
    this.popupSkipDays = parseInt(this.element.dataset.popupTime, 10);
    this.popupDelay = parseInt(this.element.dataset.popupDelay, 10);
    
    // Calculate delay based on test mode
    this.delay = this.testMode ? 0 : 1000 * this.popupDelay;

    // We need to avoid delaying the init fn in the editor to avoid irrelevant code executing when the editor reloads
    this.initDelay = window.Shopify.designMode ? 0 : this.delay;
    this.showPopupDelay = window.Shopify.designMode ? this.delay : 0;

    // Cache DOM elements to avoid multiple queries
    this.popupInput = this.element.querySelector('input[type="email"]');
    this.closeButton = this.element.querySelector('.popup--close-btn');
    this.fullscreenCloseButton = this.element.querySelector('.popup--close');
    this.slideUpElement = document.querySelector('#slide-up');
    this.fullscreenElement = document.querySelector('#fullscreen');

    // Initialize focus timeout tracker
    this.focusTimeout = null;

    // Set up event bindings
    this.closeButton?.addEventListener('click', this.closePopup);
    this.fullscreenCloseButton?.addEventListener('click', this.closePopup);
    window.addEventListener('resize', this.onWindowResize);

    // Initialize the popup functionality
    this.initPopup();
  }

  /**
   * Check if local storage is available in the browser
   * @returns {boolean} True if localStorage is supported
   */
  _isLocalStorageSupported = () => {
    try {
      return typeof Storage !== 'undefined' && window.localStorage;
    } catch (e) {
      return false;
    }
  }

  /**
   * Determine if the popup should be displayed based on expiry and visibility settings
   * @returns {boolean} True if popup should show
   */
  shouldShowPopup = () => {
    if (this.testMode) return true;
    if (this.hasLocalStorage && localStorage.getItem(this.VISIBILITY_KEY) === 'true') {
      return true;
    } else if (this.isExpiredOrNotSet()) {
      return true;
    }
    return false;
  }

  /**
   * Check if the popup expiry date has passed or hasn't been set
   * @returns {boolean} True if expired or not set
   */
  isExpiredOrNotSet = () => {
    if (!this.hasLocalStorage) return true;

    const popupInfo = localStorage.getItem(this.STORAGE_KEY);
    if (popupInfo) {
      try {
        const expiry = Date.parse(JSON.parse(popupInfo).expiry);
        return expiry <= Date.now();
      } catch (e) {
        console.warn('Unable to read popup expiry date', e);
      }
    }
    return true;
  }

  /**
   * Initialize the popup and show it if conditions are met
   */
  initPopup = () => {
    if (this.shouldShowPopup()) {
      setTimeout(() => {
        this.setExpiry(this.popupSkipDays);
        this.show();
      }, this.initDelay);
    }
  }

  /**
   * Store the expiry date for the popup in local storage
   * @param {number} days - Number of days until popup should show again
   */
  setExpiry = (days) => {
    if (this.hasLocalStorage) {
      try {
        const expiry = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify({ expiry: expiry.toISOString() }));
      } catch (e) {
        console.warn('Unable to set popup expiry:', e);
      }
    }
  }

  /**
   * Update the visibility state in local storage
   * @param {boolean} visible - Whether the popup is currently visible
   */
  updateVisibilityStorage = (visible) => {
    if (this.hasLocalStorage) {
      if (visible) {
        localStorage.setItem(this.VISIBILITY_KEY, 'true');
      } else {
        localStorage.removeItem(this.VISIBILITY_KEY);
      }
    }
  }

  /**
   * Check if the current viewport is mobile size
   * @returns {boolean} True if viewport width is less than 769px
   */
  isMobileView = () => {
    return window.innerWidth < 769;
  }

  /**
   * Handle window resize events to adjust popup display
   */
  onWindowResize = () => {
    if (this.shouldShowPopup()) {
      this.show();
    }
  }

  /**
   * Display the popup in the appropriate format (slide-up or fullscreen)
   */
  show = () => {
    this.closePopup(); // Ensure the previous popup state is cleaned up
    if (this.isMobileView() || !this.isFullscreen) {
      setTimeout(() => {
        this.showSlideUp();
      }, this.showPopupDelay);
    } else {
      setTimeout(() => {
        this.showFullscreen();
      }, this.showPopupDelay);
    }
    this.updateVisibilityStorage(true);
  }

  /**
   * Display the popup in slide-up format (mobile or non-fullscreen)
   */
  showSlideUp = () => {
    if (this.slideUpElement) {
      this.slideUpElement.style.visibility = 'visible';
    }
    this.element.classList.add('slide-up', 'visible');
  }

  /**
   * Display the popup in fullscreen format (desktop only)
   */
  showFullscreen = () => {
    this.element.classList.add('fullscreen', 'visible');
    if (this.fullscreenElement) {
      this.fullscreenElement.style.display = 'flex';
      document.body.classList.add('fullscreen-popup-open');
      document.querySelector('#PopupOverlay')?.addEventListener('click', this.closePopup);
    }
  }

  /**
   * Close and hide the popup, cleaning up all display states
   */
  closePopup = () => {
    this.element.classList.remove('visible', 'fullscreen', 'slide-up');
    if (this.fullscreenElement) {
      this.fullscreenElement.style.display = 'none';
      document.body.classList.remove('fullscreen-popup-open');
      document.querySelector('#PopupOverlay')?.removeEventListener('click', this.closePopup);
    }
    if (this.slideUpElement) {
      this.slideUpElement.style.visibility = 'hidden';
    }
    this.updateVisibilityStorage(false);
    // Clear the focus timeout if it exists
    if (this.focusTimeout) {
      clearTimeout(this.focusTimeout);
      this.focusTimeout = null;
    }
  }
}
